import { Plus, Pencil, Trash2 } from "lucide-react";
import { fmt } from "@/lib/api";
import { useRegion } from "@/context/RegionContext";

const TITLES = { panel: ["Solar panels", "Output watts and module dimensions feed the array size and roof footprint."], inverter: ["Inverters", "Rated kW. The engine picks the smallest unit above peak load × margin."], battery: ["Batteries", "Usable kWh. Sized against average night-time consumption."] };

export default function CatalogTable({ type, region, items, onAdd, onEdit, onRemove }) {
  const [title, sub] = TITLES[type];
  const { convert } = useRegion();
  const sec = region.secondary_currency;
  return (
    <div className="animate-rise" data-testid={`catalog-${type}`}>
      <div className="flex items-end justify-between pb-6 border-b border-kl-black">
        <div>
          <p className="kl-label">Catalog · <span className="text-kl-greenDark" data-testid="catalog-region-label">{region.name} · {region.currency}</span></p>
          <h2 className="font-display font-light text-3xl mt-1">{title}</h2><p className="text-sm text-kl-mutedLight mt-1">{sub}</p>
        </div>
        <button onClick={onAdd} data-testid={`catalog-add-${type}`} className="kl-btn-primary"><Plus size={15} /> Add {type}</button>
      </div>
      <table className="w-full text-sm mt-2">
        <thead>
          <tr className="kl-label text-left">
            <th className="py-3 font-normal">Brand / model</th><th className="py-3 font-normal text-right">Rating</th>
            {type === "panel" && <><th className="py-3 font-normal text-right">Efficiency</th><th className="py-3 font-normal text-right">Size (mm)</th></>}
            <th className="py-3 font-normal text-right">Price ({region.currency})</th><th className="py-3 font-normal text-right">≈ {sec}</th><th className="py-3 font-normal text-right">Default</th><th className="py-3 w-24" />
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-kl-mutedLight" data-testid="catalog-empty">Nothing in the {region.name} inventory yet.</td></tr>}
          {items.map((it) => (
            <tr key={it.id} className="kl-row group" data-testid={`catalog-row-${it.id}`}>
              <td className="py-3"><div className="font-display">{it.model}</div><div className="text-xs text-kl-mutedLight">{it.brand}{it.notes ? ` · ${it.notes}` : ""}</div></td>
              <td className="py-3 text-right font-mono">{it.rating} {it.unit}</td>
              {type === "panel" && <><td className="py-3 text-right font-mono">{it.efficiency ?? "—"}%</td><td className="py-3 text-right font-mono text-xs">{it.width_mm ?? "—"} × {it.height_mm ?? "—"}</td></>}
              <td className="py-3 text-right font-mono">{fmt.money(it.price, region.currency)}</td>
              <td className="py-3 text-right font-mono text-xs text-kl-mutedLight">{fmt.money(convert(it.price, region.currency, sec), sec)}</td>
              <td className="py-3 text-right">{it.is_default && <span className="font-mono text-[10px] uppercase tracking-widest text-kl-greenDark border border-kl-greenDark px-1.5 py-0.5" data-testid={`catalog-default-${it.id}`}>default</span>}</td>
              <td className="py-3 text-right whitespace-nowrap">
                <button onClick={() => onEdit(it)} data-testid={`catalog-edit-${it.id}`} className="p-2 hover:bg-kl-black hover:text-white transition-colors duration-150"><Pencil size={14} /></button>
                <button onClick={() => onRemove(it)} data-testid={`catalog-remove-${it.id}`} className="p-2 hover:bg-kl-inactive hover:text-white transition-colors duration-150"><Trash2 size={14} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
